import { useState, useEffect, useCallback } from 'react';
import type { NetworkOption } from '@/lib/tezos';

const STORAGE_KEY = 'tezos-marketplace-network';

function getStoredNetwork(): NetworkOption {
  if (typeof window === 'undefined') return 'mainnet';
  const stored = localStorage.getItem(STORAGE_KEY) as NetworkOption | null;
  if (stored === 'mainnet' || stored === 'ghostnet' || stored === 'sandbox') return stored;

  // Default to mainnet
  const envNet = (import.meta.env.VITE_NETWORK as NetworkOption) || 'mainnet';
  if (envNet === 'mainnet' || envNet === 'ghostnet' || envNet === 'sandbox') return envNet;
  return 'mainnet';
}

/**
 * Hook to manage the app's network state.
 * This is the single source of truth for which network the app is operating on.
 * It controls both wallet connection and which collections are displayed.
 */
export function useAppNetwork() {
  const [network, setNetworkState] = useState<NetworkOption>(getStoredNetwork);

  // Persist to localStorage and dispatch event for cross-component sync
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, network);
    window.dispatchEvent(new CustomEvent('app-network-change', { detail: network }));
  }, [network]);

  // Listen for changes from other hook instances
  useEffect(() => {
    const handler = (e: CustomEvent<NetworkOption>) => setNetworkState(e.detail);
    window.addEventListener('app-network-change', handler as EventListener);
    return () => window.removeEventListener('app-network-change', handler as EventListener);
  }, []);

  const setNetwork = useCallback((net: NetworkOption) => setNetworkState(net), []);

  return { network, setNetwork };
}

/**
 * Get the current app network from localStorage (for non-hook usage)
 */
export function getAppNetwork(): NetworkOption {
  return getStoredNetwork();
}
